/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import { Calendar, ToolbarProps, View, dateFnsLocalizer, SlotInfo } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { format, startOfWeek, getDay, parseISO } from 'date-fns';
import enUS from 'date-fns/locale/en-US';
import {
  getUnavailabilitySlots,
  createUnavailabilitySlot,
  deleteUnavailabilitySlot,
} from '../../services/providerServices';
import { Button, Modal } from 'react-bootstrap';
import {
  CALENDAR_VIEWS,
  generateCustomTimeRanges,
  slotCombinationLabel,
  SlotLabelCombination,
} from '../../utils/providerCalendar';
import { useKeycloak } from '@react-keycloak/web';
import { SlotCombination, SlotLabels, TimeSlot } from '../../resources/providerCalendar';
import FormButton from '@components/form/FormButton';

interface Event {
  title: string;
  start: Date;
  end: Date;
  allDay: boolean;
  slotId: number;
  SecondTitle: string;
}
interface CustomToolbarProps extends ToolbarProps<Event> {
  view: View;
}

const locales = { 'en-US': enUS };
const localizer = dateFnsLocalizer({
  format,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 0 }),
  getDay,
  locales,
});

const timeRanges = generateCustomTimeRanges();
interface ProviderCalendarProps {
  hideSaturday: boolean;
  hideSunday: boolean;
  providerId: number;
  dateFrom: string;
  slotId: number;
}

const CustomToolbar: React.FC<CustomToolbarProps> = ({ label, onNavigate, onView, view }) => {
  const [activeView, setActiveView] = useState<View>(view);

  useEffect(() => {
    setActiveView(view);
  }, [view]);

  const handleViewChange = (view: View) => {
    setActiveView(view);
    onView(view);
  };

  return (
    <div className='rtb-toolbar'>
      <div className='rtb-toolbar-top'>
        <p>Set Schedule</p>
        <div className='rtb-cal-btn'>
          <button
            type='button'
            className={`rbc-btn ${activeView === 'day' ? 'active' : ''}`}
            onClick={() => handleViewChange('day')}
          >
            Day
          </button>
          <button
            type='button'
            className={`rbc-btn ${activeView === 'week' ? 'active' : ''}`}
            onClick={() => handleViewChange('week')}
          >
            Week
          </button>
          <button
            type='button'
            className={`rbc-btn ${activeView === 'month' ? 'active' : ''}`}
            onClick={() => handleViewChange('month')}
          >
            Month
          </button>
        </div>
      </div>
      <div className='rtb-navigation'>
        <button className='rtb-nav-button' onClick={() => onNavigate('PREV')}>
          <span className='rtb-nav-icon'>&#8249;</span>
          Prev
        </button>
        <span className='rtb-toolbar-label'>{label}</span>
        <button className='rtb-nav-button' onClick={() => onNavigate('NEXT')}>
          Next
          <span className='rtb-nav-icon'>&#8250;</span>
        </button>
      </div>
    </div>
  );
};

const CustomEvent: React.FC<{
  event: {
    event: {
      start: Date;
      title: string;
      SecondTitle: string;
      slotId: number;
    };
  };
  showDeleteModal: boolean;
  setShowDeleteModal: (show: boolean) => void;
  setSelectedEventId: (slotId: number) => void;
  setSelectedSlotTime: (slotTime: string) => void;
}> = ({ event, showDeleteModal, setShowDeleteModal, setSelectedEventId, setSelectedSlotTime }) => {
  const dayOfWeek = getDay(event.event.start);
  const colors = [
    { background: 'rgba(255, 102, 102, 0.2)', border: 'rgba(255, 102, 102, 5)', font: '#660000' },
    { background: 'rgba(153, 255, 102, 0.2)', border: 'rgba(153, 255, 102, 5)', font: '#336600' },
    { background: 'rgba(255, 204, 102, 0.2)', border: 'rgba(255, 204, 102, 5)', font: '#666600' },
    { background: 'rgba(255, 153, 102, 0.2)', border: 'rgba(255, 153, 102, 5)', font: '#663300' },
    { background: 'rgba(102, 255, 153, 0.2)', border: 'rgba(102, 255, 153, 5)', font: '#006633' },
    { background: 'rgba(102, 204, 255, 0.2)', border: 'rgba(102, 204, 255, 5)', font: '#003366' },
    { background: 'rgba(204, 102, 255, 0.2)', border: 'rgba(204, 102, 255, 5)', font: '#660066' },
  ];
  const { background, border, font } = colors[dayOfWeek] || colors[0];
  const handleClick = () => {
    if (event.event.slotId !== null && event.event.slotId !== undefined) {
      if (!showDeleteModal) {
        setSelectedEventId(event.event.slotId);
        setSelectedSlotTime(event.event.title);
        setShowDeleteModal(true);
      }
    }
  };

  return (
    <div
      className='custom-event'
      onClick={handleClick}
      style={{
        backgroundColor: background,
        borderColor: border,
        borderWidth: '8px',
        borderStyle: 'solid',
      }}
    >
      <div className='shift-time' style={{ color: font }}>
        {event.event.title}
      </div>
      <div className='shift-title' style={{ color: font }}>
        {event.event.SecondTitle}
      </div>
    </div>
  );
};

const ProviderCalendar: React.FC<ProviderCalendarProps> = ({
  hideSaturday,
  hideSunday,
  providerId,
  dateFrom,
  slotId,
}) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [firstSelectedTimeRange, setfirstSelectedTimeRange] = useState<{
    label: string;
    from: string;
    to: string;
  } | null>(null);
  const [secondSelectedTimeRange, setsecondSelectedTimeRange] = useState<{
    label: string;
    from: string;
    to: string;
  } | null>(null);
  const [events, setEvents] = useState<Event[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [currentView, setCurrentView] = useState<View>('month');
  const [isLoading, setIsLoading] = useState(false);
  const [isAllDay, setIsAllDay] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedSlotTime, setSelectedSlotTime] = useState('');
  const { keycloak } = useKeycloak();

  const fetchUnavailabilitySlotDetails = async () => {
    try {
      const response = await getUnavailabilitySlots(providerId);
      const groupedSlotsByDate = new Map<string, any[]>();

      response.forEach((slot: any) => {
        const date = slot.date_from;
        const slotJson = slot.slot_json;

        const { title, SecondTitle } = slotCombinationLabel(slotJson);

        const sortedSlots = [...slotJson].sort((a, b) => a.from_slot.localeCompare(b.from_slot));

        const firstSlot = sortedSlots[0];
        const lastSlot = sortedSlots[sortedSlots.length - 1];

        const start = parseISO(`${date}T${firstSlot.from_slot}`);
        const end = parseISO(`${date}T${lastSlot.to_slot}`);

        const mergedEvent = {
          title,
          SecondTitle,
          start,
          end,
          allDay: false,
          slotId: slot.id,
        };

        if (groupedSlotsByDate.has(date)) {
          groupedSlotsByDate.get(date)?.push(mergedEvent);
        } else {
          groupedSlotsByDate.set(date, [mergedEvent]);
        }
      });

      const mergedEvents = Array.from(groupedSlotsByDate.values()).flat();

      setEvents(mergedEvents);
      setError(null);
    } catch (error) {
      setError('Oops, Failed to fetch unavailability slots. Please contact admin.');
    }
  };
  useEffect(() => {
    fetchUnavailabilitySlotDetails();
  }, [providerId, dateFrom, slotId]);

  const dayPropGetter = (date: Date) => {
    if (currentView !== 'month') {
      if ((hideSaturday && getDay(date) === 6) || (hideSunday && getDay(date) === 0)) {
        return { className: 'hidden-day' };
      }
    }
    return {};
  };

  const handleSelectSlot = (slotInfo: SlotInfo) => {
    if (slotInfo.box) {
      setSelectedDate(slotInfo.start);

      const dayOfWeek = getDay(slotInfo.start);
      let menuLeftPosition = slotInfo.box.clientX;

      if (dayOfWeek === 4 || dayOfWeek === 5 || dayOfWeek === 6) {
        menuLeftPosition -= 160;
      } else if (dayOfWeek === 3) {
        menuLeftPosition =
          slotInfo.box.clientX - (document.querySelector('.menu-container')?.clientWidth || 0) / 2;
      }

      setMenuPosition({ top: slotInfo.box.clientY, left: menuLeftPosition });
      setIsMenuOpen(true);
    }
  };

  const handleSubmitTimeRange = async () => {
    if (!selectedDate) return;

    const formattedDate = format(selectedDate, 'yyyy-MM-dd');

    let slotJson: {
      date: string;
      from_slot: string;
      to_slot: string;
    }[] = [];

    if (isAllDay) {
      slotJson.push({
        date: formattedDate,
        from_slot: TimeSlot.AllDay.split('-')[0],
        to_slot: TimeSlot.AllDay.split('-')[1],
      });
    } else {
      if (firstSelectedTimeRange) {
        slotJson.push({
          date: formattedDate,
          from_slot: firstSelectedTimeRange.from,
          to_slot: firstSelectedTimeRange.to,
        });
      }
      if (secondSelectedTimeRange) {
        slotJson.push({
          date: formattedDate,
          from_slot: secondSelectedTimeRange.from,
          to_slot: secondSelectedTimeRange.to,
        });
      }
    }

    slotJson = slotJson.filter(
      (slot) => slot.from_slot && slot.to_slot && slot.from_slot < slot.to_slot,
    );

    if (slotJson.length === 0) {
      setError('Please select valid time ranges before submitting.');
      return;
    }

    const params = {
      date_from: formattedDate,
      date_to: formattedDate,
      slot_json: slotJson,
      create_username: keycloak?.tokenParsed?.preferred_username,
    };

    try {
      setIsLoading(true);
      await createUnavailabilitySlot(providerId, params);
      await fetchUnavailabilitySlotDetails();

      setIsMenuOpen(false);
      setfirstSelectedTimeRange(null);
      setsecondSelectedTimeRange(null);
      setIsAllDay(false);
      setError(null);
    } catch (error) {
      setError('Oops, Failed to create unavailability slots. Please contact admin.');
    } finally {
      setIsLoading(false);
    }
  };

  const [isDeleting, setIsDeleting] = useState(false);
  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    if (selectedEventId) {
      try {
        await deleteUnavailabilitySlot(providerId, selectedEventId);

        await fetchUnavailabilitySlotDetails();

        setShowDeleteModal(false);
        setSelectedEventId(null);
        setSelectedSlotTime('');
      } catch (error) {
        console.error('Error deleting record:', error);
        setError('Oops, Failed to delete unavailability slot. Please contact admin.');
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const isConfirmButtonEnabled = () => {
    if (isAllDay) {
      return !!selectedDate;
    }
    const isValidSet = Object.values(SlotCombination).some((combination) => {
      const labels = SlotLabelCombination[combination];
      return (
        firstSelectedTimeRange?.label === labels.first &&
        secondSelectedTimeRange?.label === labels.second
      );
    });

    return !!selectedDate && !!firstSelectedTimeRange && !!secondSelectedTimeRange && isValidSet;
  };

  return (
    <div>
      {error && <div className='error-message'>{error}</div>}
      <Calendar
        localizer={localizer}
        startAccessor='start'
        endAccessor='end'
        style={{ height: 585 }}
        defaultView='month'
        views={CALENDAR_VIEWS}
        dayPropGetter={dayPropGetter}
        components={{
          toolbar: (props) => {
            useEffect(() => {
              setCurrentView(props.view);
            }, [props.view]);

            return <CustomToolbar {...props} view={currentView} />;
          },
          event: (event) => (
            <CustomEvent
              event={event}
              showDeleteModal={showDeleteModal}
              setShowDeleteModal={setShowDeleteModal}
              setSelectedEventId={setSelectedEventId}
              setSelectedSlotTime={setSelectedSlotTime}
            />
          ),
        }}
        selectable
        onSelectSlot={handleSelectSlot}
        events={events}
      />
      {isMenuOpen && selectedDate && (
        <div className='menu-container' style={{ top: menuPosition.top, left: menuPosition.left }}>
          <div className='menu-content'>
            <div className='menu-header'>
              <div className='selected-date'>
                {selectedDate && format(selectedDate, 'MMMM d, yyyy')}
              </div>
              <button
                className='close-menu'
                onClick={() => {
                  setIsMenuOpen(false);
                  setfirstSelectedTimeRange(null);
                  setsecondSelectedTimeRange(null);
                }}
              >
                X
              </button>
            </div>

            <div>
              <select
                value={firstSelectedTimeRange ? firstSelectedTimeRange.label : ''}
                onChange={(e) => {
                  const selectedRange = Object.values(timeRanges)
                    .flat()
                    .find((range) => range.label === e.target.value);
                  setfirstSelectedTimeRange(selectedRange || null);
                }}
                disabled={isAllDay}
              >
                <option value=''>Select First Slot</option>
                {Object.values(timeRanges)
                  .flat()
                  .map((range, index) => (
                    <option key={index} value={range.label}>
                      {range.label}
                    </option>
                  ))}
              </select>
            </div>
            <div>
              <select
                value={secondSelectedTimeRange ? secondSelectedTimeRange.label : ''}
                onChange={(e) => {
                  const selectedRange = Object.values(timeRanges)
                    .flat()
                    .find((range) => range.label === e.target.value);
                  setsecondSelectedTimeRange(selectedRange || null);
                }}
                disabled={isAllDay}
              >
                <option value=''>Select Second Slot</option>
                {Object.values(timeRanges)
                  .flat()
                  .map((range, index) => (
                    <option key={index} value={range.label}>
                      {range.label}
                    </option>
                  ))}
              </select>
            </div>
            <div>
              <label className='all-day-label'>
                <input
                  type='checkbox'
                  checked={isAllDay}
                  onChange={(e) => {
                    setIsAllDay(e.target.checked);
                    if (e.target.checked) {
                      setfirstSelectedTimeRange({ label: '', from: '00:00', to: '23:59' });
                      setsecondSelectedTimeRange(null);
                    } else if (firstSelectedTimeRange?.label === '') {
                      setfirstSelectedTimeRange(null);
                    }
                  }}
                />
                All Day
              </label>
            </div>
            <Button
              variant='primary'
              onClick={handleSubmitTimeRange}
              disabled={!isConfirmButtonEnabled() || isLoading}
            >
              {isLoading ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      )}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        className='d-flex align-items-center justify-content-center'
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete Slot</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete slot <span className='bold-text'>{selectedSlotTime}</span>
          ?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant='outlined'
            className='cancel-button'
            onClick={() => setShowDeleteModal(false)}
          >
            No, Cancel
          </Button>
          <FormButton
            variant='danger'
            loading={isDeleting}
            onClick={handleConfirmDelete}
            disabled={isDeleting}
          >
            Yes, Delete
          </FormButton>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ProviderCalendar;
